import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Fab,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as OutsourceIcon,
  AssignmentReturn as ReceivedIcon,
  AttachMoney as GoldsmithPaymentIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import {
  getRepairOrders,
  updateRepairOrderStatus,
  markRepairOrderDelivered,
  outsourceRepairOrder,
  markRepairOrderReceived,
  addGoldsmithPayment
} from '../redux/actions/repairOrderActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';
import { getCustomers } from '../redux/actions/customerActions';
import { getGoldsmiths } from '../redux/actions/goldsmithActions';

const RepairOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { repairOrders, loading, error } = useSelector(state => state.repairOrders);
  const { activePaymentModes } = useSelector(state => state.paymentModes);
  const { customers } = useSelector(state => state.customers);
  const { goldsmiths } = useSelector(state => state.goldsmith);
  const { token } = useSelector(state => state.auth);

  // Local state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedRepairOrder, setSelectedRepairOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [deliveryData, setDeliveryData] = useState({
    delivered_date: '',
    delivered_to: '',
    delivery_notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    payment_modes: []
  });
  const [deliveryPaymentModes, setDeliveryPaymentModes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  
  // Outsource related states
  const [outsourceDialogOpen, setOutsourceDialogOpen] = useState(false);
  const [receivedDialogOpen, setReceivedDialogOpen] = useState(false);
  const [goldsmithPaymentDialogOpen, setGoldsmithPaymentDialogOpen] = useState(false);
  const [outsourceData, setOutsourceData] = useState({
    goldsmith_id: '',
    goldsmith_charges: '',
    expected_return_date: '',
    outsource_notes: '',
    payment_status: 'pending',
    payments: []
  });
  const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);

  // Payment mode icons mapping
  const paymentIcons = {
    cash: '💰',
    card: '💳',
    upi: '📱',
    netbanking: '🏦',
    cheque: '📄',
    wallet: '👛',
    neft: '💸',
    rtgs: '⚡'
  };
  const [receivedData, setReceivedData] = useState({
    actual_return_date: '',
    received_notes: ''
  });
  const [goldsmithPaymentData, setGoldsmithPaymentData] = useState({
    payment_mode_code: '',
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    reference_number: '',
    notes: ''
  });

  // Load data
  const loadRepairOrders = useCallback(() => {
    const filters = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      status: statusFilter,
      customerId: customerFilter,
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null
    };

    dispatch(getRepairOrders(filters));
  }, [dispatch, page, rowsPerPage, searchTerm, statusFilter, customerFilter, startDate, endDate]);

  useEffect(() => {
    dispatch(getActivePaymentModes());
    dispatch(getCustomers());
    dispatch(getGoldsmiths());
  }, [dispatch]);

  useEffect(() => {
    loadRepairOrders();
  }, [loadRepairOrders]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        loadRepairOrders();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, loadRepairOrders]);

  // Event handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    loadRepairOrders();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCustomerFilter('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleRefresh = () => {
    loadRepairOrders();
  };

  const handleActionMenuClick = (event, repairOrder) => {
    setAnchorEl(event.currentTarget);
    setSelectedRepairOrder(repairOrder);
    setActionMenuOpen(true);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
    setActionMenuOpen(false);
    setSelectedRepairOrder(null);
  };

  const handleStatusUpdate = () => {
    console.log('Opening status update dialog for order:', selectedRepairOrder);
    if (!selectedRepairOrder) {
      console.error('No repair order selected!');
      return;
    }
    setStatusDialogOpen(true);
    setNewStatus(selectedRepairOrder.status || '');
    setStatusNotes('');
    // Only close the menu, don't clear the selected order
    setAnchorEl(null);
    setActionMenuOpen(false);
  };

  const handleDeliveryUpdate = () => {
    if (!selectedRepairOrder) return;
    
    setDeliveryDialogOpen(true);
    setDeliveryData({
      delivered_date: format(new Date(), 'yyyy-MM-dd'),
      delivered_to: selectedRepairOrder?.customer?.name || '',
      delivery_notes: ''
    });
    setPaymentData({
      payment_modes: []
    });
    setDeliveryPaymentModes([]);
    
    // Close action menu but preserve selectedRepairOrder
    setAnchorEl(null);
    setActionMenuOpen(false);
  };

  const handleStatusSubmit = async () => {
    if (!selectedRepairOrder || !newStatus) {
      console.log('Missing required data:', { selectedRepairOrder, newStatus });
      return;
    }

    console.log('=== STARTING STATUS UPDATE ===');
    console.log('Order ID:', selectedRepairOrder.id);
    console.log('Current Status:', selectedRepairOrder.status);
    console.log('New Status:', newStatus);
    console.log('Notes:', statusNotes);

    try {
      console.log('Dispatching Redux action...');
      const result = await dispatch(updateRepairOrderStatus(selectedRepairOrder.id, {
        status: newStatus,
        notes: statusNotes
      }));
      
      console.log('Redux action completed. Result:', result);
      
      if (result && result.success !== false) {
        console.log('Status update successful, refreshing list...');
        setStatusDialogOpen(false);
        setNewStatus('');
        setStatusNotes('');
        // Clear the selected order after successful update
        setSelectedRepairOrder(null);
        // Refresh the repair orders list
        dispatch(getRepairOrders({
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter,
          customerId: customerFilter,
          startDate: startDate,
          endDate: endDate,
          search: searchTerm
        }));
      } else {
        console.error('Status update failed with result:', result);
      }
    } catch (error) {
      console.error('Status update error:', error);
    }
    
    console.log('=== STATUS UPDATE COMPLETED ===');
  };

  const handlePaymentModeChange = (modeCode, amount) => {
    setPaymentData(prev => {
      const existing = prev.payment_modes.find(p => p.payment_mode_code === modeCode);
      if (existing) {
        if (amount === '') {
          return {
            ...prev,
            payment_modes: prev.payment_modes.filter(p => p.payment_mode_code !== modeCode)
          };
        }
        return {
          ...prev,
          payment_modes: prev.payment_modes.map(p => 
            p.payment_mode_code === modeCode 
              ? { ...p, amount: parseFloat(amount) || 0 }
              : p
          )
        };
      } else if (amount !== '') {
        return {
          ...prev,
          payment_modes: [...prev.payment_modes, {
            payment_mode_code: modeCode,
            amount: parseFloat(amount) || 0,
            payment_date: format(new Date(), 'yyyy-MM-dd')
          }]
        };
      }
      return prev;
    });
  };

  const getPaymentAmount = (modeCode) => {
    const payment = paymentData.payment_modes.find(p => p.payment_mode_code === modeCode);
    return payment ? payment.amount.toString() : '';
  };

  const calculateExistingPaid = () => {
    return selectedRepairOrder?.payments?.reduce((sum, payment) => sum + Number(payment.amount || 0), 0) || 0;
  };

  const calculateNewPaid = () => {
    return deliveryPaymentModes.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  };

  const calculateTotalPaid = () => {
    return calculateExistingPaid() + calculateNewPaid();
  };

  const calculateBalance = () => {
    const estimatedCost = Number(selectedRepairOrder?.estimated_cost) || 0;
    const totalPaid = calculateTotalPaid();
    return estimatedCost - totalPaid;
  };

  const handleDeliverySubmit = async () => {
    if (!selectedRepairOrder) return;

    const balance = calculateBalance();
    if (balance > 0) {
      alert(`Outstanding balance of ₹${balance.toFixed(2)} must be cleared before delivery.`);
      return;
    }

    try {
      const submitData = {
        ...deliveryData,
        payments: deliveryPaymentModes
          .filter(pm => pm.amount && parseFloat(pm.amount) > 0)
          .map(pm => ({
            ...pm,
            payment_date: deliveryData.delivered_date || new Date().toISOString().split('T')[0]
          }))
      };
      
      console.log('=== DELIVERY DEBUG ===');
      console.log('Selected Repair Order ID:', selectedRepairOrder.id);
      console.log('Submit Data:', submitData);
      console.log('Payment Modes:', deliveryPaymentModes);
      console.log('=====================');
      
      const result = await dispatch(markRepairOrderDelivered(selectedRepairOrder.id, submitData));
      console.log('Delivery Result:', result);
      
      if (result.success) {
        setDeliveryDialogOpen(false);
        // Clear the selected repair order to refresh the list
        setSelectedRepairOrder(null);
        // Reload the repair orders list
        loadRepairOrders();
      } else {
        alert(result.message || 'Failed to mark repair order as delivered');
      }
    } catch (error) {
      console.error('Error marking as delivered:', error);
      alert('Error marking as delivered. Check console for details.');
    }
  };

  const handleGenerateReceipt = () => {
    if (!selectedRepairOrder) return;
    
    // Generate receipt URL - use the API base URL directly
    const baseUrl = import.meta.env.REACT_APP_API_URL || 'https://jewel-posapi.appsinlive.in/api';
    const receiptUrl = `${baseUrl}/repair-orders/${selectedRepairOrder.id}/receipt`;
    
    // Add auth token to URL for authentication - check both Redux state and localStorage
    const authToken = token || localStorage.getItem('token');
    const finalUrl = authToken ? `${receiptUrl}?token=${authToken}` : receiptUrl;
    
    console.log('Generating receipt URL:', finalUrl);
    // Open receipt in new window
    window.open(finalUrl, '_blank');
    
    handleActionMenuClose();
  };

  // Outsource handlers
  const handleOutsource = () => {
    if (!selectedRepairOrder) return;
    
    
    setOutsourceData({
      goldsmith_id: '',
      goldsmith_charges: '',
      expected_return_date: '',
      outsource_notes: '',
      payment_status: 'pending',
      payments: []
    });
    setSelectedPaymentModes([]);
    setOutsourceDialogOpen(true);
    
    // Close action menu but preserve selectedRepairOrder
    setAnchorEl(null);
    setActionMenuOpen(false);
    // Don't call handleActionMenuClose() as it clears selectedRepairOrder
  };

  // Handle payment mode selection for outsource
  const handlePaymentModeToggle = (mode) => {
    setSelectedPaymentModes(prev => {
      const isSelected = prev.some(pm => pm.payment_mode_code === mode.code);
      if (isSelected) {
        return prev.filter(pm => pm.payment_mode_code !== mode.code);
      } else {
        return [...prev, {
          payment_mode_code: mode.code,
          amount: '',
          reference_number: '',
          bank_details: '',
          payment_date: format(new Date(), 'yyyy-MM-dd')
        }];
      }
    });
  };

  // Handle payment amount change
  const handlePaymentAmountChange = (modeCode, amount) => {
    setSelectedPaymentModes(prev => 
      prev.map(pm => 
        pm.payment_mode_code === modeCode 
          ? { ...pm, amount: amount }
          : pm
      )
    );
  };

  // Handle payment reference change
  const handlePaymentReferenceChange = (modeCode, referenceNumber) => {
    setSelectedPaymentModes(prev => 
      prev.map(pm => 
        pm.payment_mode_code === modeCode 
          ? { ...pm, reference_number: referenceNumber }
          : pm
      )
    );
  };

  // Calculate total payment amount
  const getTotalPaymentAmount = () => {
    return selectedPaymentModes.reduce((total, pm) => total + parseFloat(pm.amount || 0), 0);
  };

  // Delivery payment mode handlers
  const handleDeliveryPaymentModeToggle = (mode) => {
    setDeliveryPaymentModes(prev => {
      const isSelected = prev.some(pm => pm.payment_mode_code === mode.code);
      if (isSelected) {
        return prev.filter(pm => pm.payment_mode_code !== mode.code);
      } else {
        return [...prev, {
          payment_mode_code: mode.code,
          amount: '',
          reference_number: ''
        }];
      }
    });
  };

  const handleDeliveryPaymentAmountChange = (paymentModeCode, amount) => {
    setDeliveryPaymentModes(prev => 
      prev.map(payment => 
        payment.payment_mode_code === paymentModeCode 
          ? { ...payment, amount }
          : payment
      )
    );
  };

  const handleDeliveryPaymentReferenceChange = (paymentModeCode, referenceNumber) => {
    setDeliveryPaymentModes(prev => 
      prev.map(payment => 
        payment.payment_mode_code === paymentModeCode 
          ? { ...payment, reference_number: referenceNumber }
          : payment
      )
    );
  };

  const getDeliveryTotalPaymentAmount = () => {
    return deliveryPaymentModes.reduce((total, payment) => total + (parseFloat(payment.amount) || 0), 0);
  };

  const handleMarkReceived = () => {
    if (!selectedRepairOrder) return;
    setReceivedData({
      actual_return_date: format(new Date(), 'yyyy-MM-dd'),
      received_notes: ''
    });
    setReceivedDialogOpen(true);
    
    // Close action menu but preserve selectedRepairOrder
    setAnchorEl(null);
    setActionMenuOpen(false);
  };

  const handleGoldsmithPayment = () => {
    if (!selectedRepairOrder) return;
    setGoldsmithPaymentData({
      payment_mode_code: '',
      amount: '',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      reference_number: '',
      notes: ''
    });
    setGoldsmithPaymentDialogOpen(true);
    
    // Close action menu but preserve selectedRepairOrder
    setAnchorEl(null);
    setActionMenuOpen(false);
  };

  const handleOutsourceSubmit = async () => {
    if (!selectedRepairOrder || !outsourceData.goldsmith_id || !outsourceData.goldsmith_charges) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const submitData = {
        ...outsourceData,
        payments: selectedPaymentModes.filter(pm => pm.amount && parseFloat(pm.amount) > 0)
      };
      
      await dispatch(outsourceRepairOrder(selectedRepairOrder.id, submitData));
      setOutsourceDialogOpen(false);
      setSelectedPaymentModes([]);
      setSelectedRepairOrder(null); // Clear selection after successful submit
      loadRepairOrders();
    } catch (error) {
      console.error('Error outsourcing repair order:', error);
    }
  };

  const handleReceivedSubmit = async () => {
    if (!selectedRepairOrder) return;

    try {
      await dispatch(markRepairOrderReceived(selectedRepairOrder.id, receivedData));
      setReceivedDialogOpen(false);
      loadRepairOrders();
    } catch (error) {
      console.error('Error marking as received:', error);
    }
  };

  const handleGoldsmithPaymentSubmit = async () => {
    if (!selectedRepairOrder || !goldsmithPaymentData.payment_mode_code || !goldsmithPaymentData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await dispatch(addGoldsmithPayment(selectedRepairOrder.id, goldsmithPaymentData));
      setGoldsmithPaymentDialogOpen(false);
      loadRepairOrders();
    } catch (error) {
      console.error('Error adding goldsmith payment:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'outsourced': return 'secondary';
      case 'completed': return 'success';
      case 'delivered': return 'primary';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <ScheduleIcon />;
      case 'in_progress': return <EditIcon />;
      case 'outsourced': return <OutsourceIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'delivered': return <DeliveryIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const calculatePaidAmount = (payments) => {
    return payments?.reduce((sum, payment) => sum + Number(payment.amount || 0), 0) || 0;
  };

  const safeFormatCurrency = (value) => {
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const filteredRepairOrders = repairOrders || [];

  if (loading && filteredRepairOrders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Repair Orders
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/repair-orders/new')}
            sx={{
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              borderRadius: 2,
              px: 3,
              py: 1
            }}
          >
            New Repair Order
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  placeholder="Search by order number, customer, or description..."
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="outsourced">Outsourced</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  fullWidth
                  options={customers || []}
                  getOptionLabel={(option) => option.name || ''}
                  value={customers?.find(customer => customer.id.toString() === customerFilter) || null}
                  onChange={(event, newValue) => {
                    setCustomerFilter(newValue ? newValue.id.toString() : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      variant="outlined"
                      placeholder="Search customers..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.phone} {option.email && `• ${option.email}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  freeSolo={false}
                  disablePortal={false}
                  noOptionsText="No customers found"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Search">
                    <IconButton onClick={handleSearch} color="primary">
                      <SearchIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear Filters">
                    <IconButton onClick={handleClearFilters} color="secondary">
                      <FilterIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} color="primary">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Repair Orders Table */}
        <Card>
          <CardContent>
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order No</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Item Description</TableCell>
                    <TableCell>Estimated Cost</TableCell>
                    <TableCell>Advance Paid</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>Delivery Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Outsource Details</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRepairOrders.map((repairOrder) => {
                    const paidAmount = calculatePaidAmount(repairOrder.payments);
                    const estimatedCost = Number(repairOrder.estimated_cost) || 0;
                    const balance = estimatedCost - paidAmount;
                    
                    return (
                      <TableRow key={repairOrder.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {repairOrder.order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {repairOrder.customer?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {repairOrder.customer?.phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {repairOrder.item_description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            ₹{safeFormatCurrency(repairOrder.estimated_cost)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            ₹{safeFormatCurrency(paidAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={balance > 0 ? 'warning.main' : 'success.main'}>
                            ₹{safeFormatCurrency(balance)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {repairOrder.delivery_date ? format(new Date(repairOrder.delivery_date), 'dd-MM-yyyy') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(repairOrder.status)}
                            label={repairOrder.status?.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(repairOrder.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {repairOrder.is_outsourced && repairOrder.goldsmith ? (
                            <Box>
                              <Typography variant="body2" fontWeight="medium" color="secondary.main">
                                {repairOrder.goldsmith.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Charges: ₹{safeFormatCurrency(repairOrder.goldsmith_charges)}
                              </Typography>
                              {repairOrder.expected_return_date && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Expected: {format(new Date(repairOrder.expected_return_date), 'dd-MM-yyyy')}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Actions">
                            <IconButton
                              onClick={(e) => handleActionMenuClick(e, repairOrder)}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRepairOrders.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </CardContent>
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={actionMenuOpen}
          onClose={handleActionMenuClose}
        >
          <MenuItem onClick={() => navigate(`/repair-orders/${selectedRepairOrder?.id}`)}>
            <ListItemIcon><ViewIcon /></ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => navigate(`/repair-orders/${selectedRepairOrder?.id}/edit`)}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText>Edit Order</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleStatusUpdate}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText>Update Status</ListItemText>
          </MenuItem>
          {selectedRepairOrder?.status === 'in_progress' && (
            <MenuItem onClick={handleOutsource}>
              <ListItemIcon><OutsourceIcon /></ListItemIcon>
              <ListItemText>Outsource to Goldsmith</ListItemText>
            </MenuItem>
          )}
          {selectedRepairOrder?.status === 'outsourced' && (
            <>
              <MenuItem onClick={handleMarkReceived}>
                <ListItemIcon><ReceivedIcon /></ListItemIcon>
                <ListItemText>Mark as Received</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleGoldsmithPayment}>
                <ListItemIcon><GoldsmithPaymentIcon /></ListItemIcon>
                <ListItemText>Add Goldsmith Payment</ListItemText>
              </MenuItem>
            </>
          )}
          {selectedRepairOrder?.status === 'completed' && (
            <MenuItem onClick={handleDeliveryUpdate}>
              <ListItemIcon><DeliveryIcon /></ListItemIcon>
              <ListItemText>Mark Delivered</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleGenerateReceipt}>
            <ListItemIcon><ReceiptIcon /></ListItemIcon>
            <ListItemText>Generate Receipt</ListItemText>
          </MenuItem>
        </Menu>

        {/* Status Update Dialog */}
        <Dialog 
          open={statusDialogOpen} 
          onClose={() => {
            console.log('Status update dialog closed via backdrop/ESC');
            setStatusDialogOpen(false);
            setSelectedRepairOrder(null);
          }} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown={loading}
        >
          <DialogTitle>
            Update Repair Order Status
            {selectedRepairOrder && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Order: {selectedRepairOrder.order_number} | Current: {selectedRepairOrder.status}
              </Typography>
            )}
          </DialogTitle>
          
          <DialogContent sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => {
                    console.log('Status changed to:', e.target.value);
                    setNewStatus(e.target.value);
                  }}
                  label="Select New Status"
                  disabled={loading}
                >
                  <MenuItem value="pending">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
                      Pending
                    </Box>
                  </MenuItem>
                  <MenuItem value="in_progress">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3' }} />
                      In Progress
                    </Box>
                  </MenuItem>
                  <MenuItem value="outsourced">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9c27b0' }} />
                      Outsourced
                    </Box>
                  </MenuItem>
                  <MenuItem value="completed">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
                      Completed
                    </Box>
                  </MenuItem>
                  <MenuItem value="cancelled">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
                      Cancelled
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Update Notes (Optional)"
                multiline
                rows={3}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                variant="outlined"
                disabled={loading}
                placeholder="Add any notes about this status change..."
              />
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button 
              onClick={() => {
                console.log('Status update dialog closed');
                setStatusDialogOpen(false);
                setSelectedRepairOrder(null);
              }} 
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                console.log('Update Status button clicked');
                handleStatusSubmit();
              }} 
              variant="contained" 
              disabled={!newStatus || loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Updating...
                </Box>
              ) : (
                'Update Status'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delivery Dialog */}
        <Dialog open={deliveryDialogOpen} onClose={() => setDeliveryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Mark Repair Order as Delivered</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Delivery Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Delivery Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Delivery Date"
                  value={deliveryData.delivered_date ? new Date(deliveryData.delivered_date) : null}
                  onChange={(date) => setDeliveryData({ ...deliveryData, delivered_date: format(date, 'yyyy-MM-dd') })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Delivered To"
                  value={deliveryData.delivered_to}
                  onChange={(e) => setDeliveryData({ ...deliveryData, delivered_to: e.target.value })}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Delivery Notes"
                  value={deliveryData.delivery_notes}
                  onChange={(e) => setDeliveryData({ ...deliveryData, delivery_notes: e.target.value })}
                  variant="outlined"
                />
              </Grid>

              {/* Payment Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Payment Clearance
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  All outstanding payments must be cleared before delivery.
                </Alert>
              </Grid>

              {/* Payment Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                      <Typography variant="h6" color="primary">
                        ₹{safeFormatCurrency(selectedRepairOrder?.estimated_cost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Already Paid</Typography>
                      <Typography variant="h6" color="info.main">
                        ₹{safeFormatCurrency(calculateExistingPaid())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">New Payment</Typography>
                      <Typography variant="h6" color="warning.main">
                        ₹{safeFormatCurrency(calculateNewPaid())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Balance</Typography>
                      <Typography variant="h6" color={calculateBalance() > 0 ? 'error.main' : 'success.main'}>
                        ₹{safeFormatCurrency(calculateBalance())}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Existing Payments Display */}
              {selectedRepairOrder?.payments && selectedRepairOrder.payments.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Existing Payments
                    </Typography>
                    {selectedRepairOrder.payments.map((payment, index) => {
                      const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                      return (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {mode?.name || payment.payment_mode_code} ({new Date(payment.payment_date).toLocaleDateString()})
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            ₹{safeFormatCurrency(payment.amount)}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Existing Payments
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        ₹{safeFormatCurrency(calculateExistingPaid())}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Payment Mode Entry */}
              {calculateBalance() > 0 && (
                <>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon color="warning" />
                        Outstanding Balance Payment
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Outstanding Balance: ₹{safeFormatCurrency(calculateBalance())}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select payment modes to clear the balance (optional for partial payments)
                      </Typography>

                      <Grid container spacing={2}>
                        {activePaymentModes?.map((mode) => {
                          const isSelected = deliveryPaymentModes.some(pm => pm.payment_mode_code === mode.code);
                          const selectedMode = deliveryPaymentModes.find(pm => pm.payment_mode_code === mode.code);

                          return (
                            <Grid item xs={12} md={6} key={mode.id}>
                              <Accordion expanded={isSelected} onChange={() => handleDeliveryPaymentModeToggle(mode)}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                                      {paymentIcons[mode.icon] || '💳'}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                      {mode.name}
                                    </Typography>
                                    {isSelected && (
                                      <Chip label="Selected" color="primary" size="small" />
                                    )}
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        label={`Amount (${mode.name})`}
                                        type="number"
                                        value={selectedMode?.amount || ''}
                                        onChange={(e) => handleDeliveryPaymentAmountChange(mode.code, e.target.value)}
                                        InputProps={{
                                          startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                        }}
                                        placeholder="0.00"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        label="Reference Number"
                                        value={selectedMode?.reference_number || ''}
                                        onChange={(e) => handleDeliveryPaymentReferenceChange(mode.code, e.target.value)}
                                        placeholder={`${mode.name} reference number`}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          );
                        })}
                      </Grid>

                      {/* Payment Summary */}
                      {deliveryPaymentModes.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Payment Summary
                          </Typography>
                          {deliveryPaymentModes.map((payment) => {
                            const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                            return (
                              <Box key={payment.payment_mode_code} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                  {mode?.name}: ₹{safeFormatCurrency(payment.amount)}
                                </Typography>
                                {payment.reference_number && (
                                  <Typography variant="caption" color="text.secondary">
                                    Ref: {payment.reference_number}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Total New Payment:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              ₹{safeFormatCurrency(getDeliveryTotalPaymentAmount())}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Remaining Balance:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color={calculateBalance() - getDeliveryTotalPaymentAmount() > 0 ? 'error.main' : 'success.main'}>
                              ₹{safeFormatCurrency(calculateBalance() - getDeliveryTotalPaymentAmount())}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </>
              )}

              {calculateBalance() <= 0 && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Payment cleared! Ready for delivery.
                  </Alert>
                </Grid>
              )}

              {/* New Payment Summary */}
              {deliveryPaymentModes.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mt: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      New Payment Summary
                    </Typography>
                    {deliveryPaymentModes.map((payment) => {
                      const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                      return (
                        <Box key={payment.payment_mode_code} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {mode?.name || payment.payment_mode_code}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            ₹{safeFormatCurrency(payment.amount)}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total New Payment
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        ₹{safeFormatCurrency(calculateNewPaid())}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Final Payment Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mt: 2, bgcolor: calculateBalance() <= 0 ? '#e8f5e8' : '#fff3cd' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                      <Typography variant="h6" color="primary">
                        ₹{safeFormatCurrency(selectedRepairOrder?.estimated_cost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                      <Typography variant="h6" color="success.main">
                        ₹{safeFormatCurrency(calculateTotalPaid())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">Final Balance</Typography>
                      <Typography variant="h6" color={calculateBalance() > 0 ? 'error.main' : 'success.main'}>
                        ₹{safeFormatCurrency(calculateBalance())}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeliveryDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeliverySubmit} 
              variant="contained"
              disabled={calculateBalance() > 0}
              color={calculateBalance() > 0 ? "error" : "success"}
              type="button"
            >
              {calculateBalance() > 0 ? `Clear Balance (₹${safeFormatCurrency(calculateBalance())})` : 'Mark Delivered'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Outsource Dialog */}
        <Dialog 
          open={outsourceDialogOpen} 
          onClose={() => setOutsourceDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <OutsourceIcon color="primary" />
              <Typography variant="h6" component="span">
                Outsource Repair Order to Goldsmith
              </Typography>
            </Box>
            {selectedRepairOrder && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Order: {selectedRepairOrder.order_number} | Customer: {selectedRepairOrder.customer?.name}
              </Typography>
            )}
          </DialogTitle>
          
          <DialogContent sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Order Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Item Description</Typography>
                      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                        {selectedRepairOrder?.item_description || 'No description available'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                      <Typography variant="h6" color="primary">
                        ₹{safeFormatCurrency(selectedRepairOrder?.estimated_cost || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Current Status</Typography>
                      <Chip
                        icon={getStatusIcon(selectedRepairOrder?.status)}
                        label={selectedRepairOrder?.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(selectedRepairOrder?.status)}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Goldsmith Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!outsourceData.goldsmith_id && outsourceDialogOpen}>
                  <InputLabel>Select Goldsmith *</InputLabel>
                  <Select
                    value={outsourceData.goldsmith_id}
                    onChange={(e) => setOutsourceData({ ...outsourceData, goldsmith_id: e.target.value })}
                    label="Select Goldsmith *"
                    disabled={loading}
                  >
                    {goldsmiths?.length > 0 ? (
                      goldsmiths.map((goldsmith) => (
                        <MenuItem key={goldsmith.id} value={goldsmith.id}>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {goldsmith.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Contact: {goldsmith.contact_person} | Phone: {goldsmith.phone}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography color="text.secondary">No goldsmiths available</Typography>
                      </MenuItem>
                    )}
                  </Select>
                  {!outsourceData.goldsmith_id && outsourceDialogOpen && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      Please select a goldsmith
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Goldsmith Charges */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Goldsmith Charges *"
                  type="number"
                  value={outsourceData.goldsmith_charges}
                  onChange={(e) => setOutsourceData({ ...outsourceData, goldsmith_charges: e.target.value })}
                  error={!outsourceData.goldsmith_charges && outsourceDialogOpen}
                  helperText={!outsourceData.goldsmith_charges && outsourceDialogOpen ? "Please enter goldsmith charges" : ""}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                  disabled={loading}
                />
              </Grid>

              {/* Expected Return Date */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Expected Return Date"
                  value={outsourceData.expected_return_date ? new Date(outsourceData.expected_return_date) : null}
                  onChange={(date) => setOutsourceData({ ...outsourceData, expected_return_date: format(date, 'yyyy-MM-dd') })}
                  minDate={new Date()}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      helperText: "When do you expect the item back from the goldsmith?"
                    }
                  }}
                />
              </Grid>

              {/* Payment Status */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={outsourceData.payment_status || 'pending'}
                    onChange={(e) => setOutsourceData({ ...outsourceData, payment_status: e.target.value })}
                    label="Payment Status"
                    disabled={loading}
                  >
                    <MenuItem value="pending">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff9800' }} />
                        Pending Payment
                      </Box>
                    </MenuItem>
                    <MenuItem value="partial">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3' }} />
                        Partial Payment
                      </Box>
                    </MenuItem>
                    <MenuItem value="paid">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                        Fully Paid
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Outsource Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Outsource Instructions"
                  multiline
                  rows={4}
                  value={outsourceData.outsource_notes}
                  onChange={(e) => setOutsourceData({ ...outsourceData, outsource_notes: e.target.value })}
                  placeholder="Enter detailed instructions for the goldsmith, including any special requirements, materials needed, quality standards, etc..."
                  disabled={loading}
                />
              </Grid>

              {/* Payment Modes Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f0f8ff', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    Advance Payment to Goldsmith
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select payment modes for advance payment to the goldsmith (optional)
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {activePaymentModes?.map((mode) => {
                      const isSelected = selectedPaymentModes.some(pm => pm.payment_mode_code === mode.code);
                      const selectedMode = selectedPaymentModes.find(pm => pm.payment_mode_code === mode.code);

                      return (
                        <Grid item xs={12} md={6} key={mode.id}>
                          <Accordion expanded={isSelected} onChange={() => handlePaymentModeToggle(mode)}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                                  {paymentIcons[mode.icon] || '💳'}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                  {mode.name}
                                </Typography>
                                {isSelected && (
                                  <Chip label="Selected" color="primary" size="small" />
                                )}
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label={`Amount (${mode.name})`}
                                    type="number"
                                    value={selectedMode?.amount || ''}
                                    onChange={(e) => handlePaymentAmountChange(mode.code, e.target.value)}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                    }}
                                    placeholder="0.00"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Reference Number"
                                    value={selectedMode?.reference_number || ''}
                                    onChange={(e) => handlePaymentReferenceChange(mode.code, e.target.value)}
                                    placeholder={`${mode.name} reference number`}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Payment Summary */}
                  {selectedPaymentModes.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Payment Summary
                      </Typography>
                      {selectedPaymentModes.map((payment) => {
                        const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                        return (
                          <Box key={payment.payment_mode_code} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {mode?.name}: ₹{safeFormatCurrency(payment.amount)}
                            </Typography>
                            {payment.reference_number && (
                              <Typography variant="caption" color="text.secondary">
                                Ref: {payment.reference_number}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total Advance Payment:
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          ₹{safeFormatCurrency(getTotalPaymentAmount())}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Cost Analysis */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Cost Analysis
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Estimated Customer Cost</Typography>
                      <Typography variant="h6" color="primary">
                        ₹{safeFormatCurrency(selectedRepairOrder?.estimated_cost || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Goldsmith Charges</Typography>
                      <Typography variant="h6" color={outsourceData.goldsmith_charges ? 'secondary.main' : 'text.disabled'}>
                        {outsourceData.goldsmith_charges ? `₹${safeFormatCurrency(outsourceData.goldsmith_charges)}` : 'Not set'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Advance Payment</Typography>
                      <Typography variant="h6" color={getTotalPaymentAmount() > 0 ? 'warning.main' : 'text.disabled'}>
                        {getTotalPaymentAmount() > 0 ? `₹${safeFormatCurrency(getTotalPaymentAmount())}` : 'None'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Potential Margin</Typography>
                      <Typography 
                        variant="h6" 
                        color={
                          outsourceData.goldsmith_charges && selectedRepairOrder?.estimated_cost 
                            ? (selectedRepairOrder.estimated_cost - outsourceData.goldsmith_charges) >= 0 ? 'success.main' : 'error.main'
                            : 'text.disabled'
                        }
                      >
                        {outsourceData.goldsmith_charges && selectedRepairOrder?.estimated_cost 
                          ? `₹${safeFormatCurrency(selectedRepairOrder.estimated_cost - outsourceData.goldsmith_charges)}`
                          : 'Not calculated'
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => {
                setOutsourceDialogOpen(false);
                setOutsourceData({
                  goldsmith_id: '',
                  goldsmith_charges: '',
                  expected_return_date: '',
                  outsource_notes: '',
                  payment_status: 'pending',
                  payments: []
                });
                setSelectedPaymentModes([]);
                setSelectedRepairOrder(null); // Clear selection when dialog closes
              }}
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleOutsourceSubmit} 
              variant="contained"
              disabled={
                !outsourceData.goldsmith_id || 
                !outsourceData.goldsmith_charges || 
                loading
              }
              sx={{
                minWidth: 140,
                background: 'linear-gradient(45deg, #9c27b0, #e91e63)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7b1fa2, #c2185b)',
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Outsourcing...
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <OutsourceIcon />
                  Outsource Order
                </Box>
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mark Received Dialog */}
        <Dialog open={receivedDialogOpen} onClose={() => setReceivedDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Mark Repair Order as Received</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Actual Return Date"
                  value={receivedData.actual_return_date ? new Date(receivedData.actual_return_date) : null}
                  onChange={(date) => setReceivedData({ ...receivedData, actual_return_date: format(date, 'yyyy-MM-dd') })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Received Notes"
                  multiline
                  rows={3}
                  value={receivedData.received_notes}
                  onChange={(e) => setReceivedData({ ...receivedData, received_notes: e.target.value })}
                  placeholder="Any notes about the received items..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReceivedDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReceivedSubmit} variant="contained">
              Mark as Received
            </Button>
          </DialogActions>
        </Dialog>

        {/* Goldsmith Payment Dialog */}
        <Dialog open={goldsmithPaymentDialogOpen} onClose={() => setGoldsmithPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Goldsmith Payment</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={goldsmithPaymentData.payment_mode_code}
                    onChange={(e) => setGoldsmithPaymentData({ ...goldsmithPaymentData, payment_mode_code: e.target.value })}
                    label="Payment Mode"
                  >
                    {activePaymentModes?.map((mode) => (
                      <MenuItem key={mode.id} value={mode.code}>
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={goldsmithPaymentData.amount}
                  onChange={(e) => setGoldsmithPaymentData({ ...goldsmithPaymentData, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Payment Date"
                  value={goldsmithPaymentData.payment_date ? new Date(goldsmithPaymentData.payment_date) : null}
                  onChange={(date) => setGoldsmithPaymentData({ ...goldsmithPaymentData, payment_date: format(date, 'yyyy-MM-dd') })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={goldsmithPaymentData.reference_number}
                  onChange={(e) => setGoldsmithPaymentData({ ...goldsmithPaymentData, reference_number: e.target.value })}
                  placeholder="Transaction reference (optional)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Payment Notes"
                  multiline
                  rows={2}
                  value={goldsmithPaymentData.notes}
                  onChange={(e) => setGoldsmithPaymentData({ ...goldsmithPaymentData, notes: e.target.value })}
                  placeholder="Any additional notes about this payment..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoldsmithPaymentDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleGoldsmithPaymentSubmit} 
              variant="contained"
              disabled={!goldsmithPaymentData.payment_mode_code || !goldsmithPaymentData.amount}
            >
              Add Payment
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default RepairOrders;

